import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FolderIcon from "@material-ui/icons/Folder";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import WarningIcon from "@material-ui/icons/Warning";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import FileModal from "../../components/FileModal";
import { financeiroStyles, KpiStrip, toneColor } from "../Financeiro/financeiroShared";
import {
  KINDS,
  kindOf,
  formatBytes,
  describeKinds,
  ACTIVE_CAMPAIGN,
  CAMPAIGN_STATUS,
} from "../../helpers/fileLists";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  ...financeiroStyles(theme),
  listCell: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
  listIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  listName: { fontWeight: 500 },
  listDesc: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    maxWidth: 240,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  thumbs: { display: "flex", alignItems: "center", gap: 3, flexShrink: 0 },
  thumbsMobile: { width: 90 },
  thumb: {
    width: 28,
    height: 28,
    borderRadius: 7,
    display: "grid",
    placeItems: "center",
    fontSize: 9,
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
  },
  thumbMissing: {
    backgroundColor: theme.palette.background.default,
    color: toneColor(theme, "amber"),
    border: `2px dashed ${toneColor(theme, "amber")}`,
  },
  thumbMore: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.secondary,
  },
  filesCell: { display: "flex", alignItems: "center", gap: 10 },
  filesText: { fontSize: 13, whiteSpace: "nowrap" },
  warnText: { fontSize: 12, fontWeight: 600, color: toneColor(theme, "amber") },
  pillBlue: {
    color: toneColor(theme, "blue"),
    backgroundColor: alpha(toneColor(theme, "blue"), theme.palette.type === "dark" ? 0.18 : 0.12),
  },
  useSub: {
    display: "block",
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 3,
    maxWidth: 180,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  dialogButton: { textTransform: "none", fontWeight: 600, borderRadius: 10 },
  mobileTags: { display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 },
  mobilePill: { height: 20, fontSize: 11, fontWeight: 600, borderRadius: 999 },
  banner: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    borderRadius: 10,
    padding: theme.spacing(1.25, 1.5),
    fontSize: 13,
    lineHeight: 1.45,
    marginTop: theme.spacing(1),
  },
  bannerRed: { backgroundColor: alpha(toneColor(theme, "red"), 0.12) },
  consequences: { margin: theme.spacing(1, 0, 0), paddingLeft: 18, lineHeight: 1.6 },
}));

// ─── Dados derivados ─────────────────────────────────────────────────────────
const enrich = (list) => {
  const options = (list.options || []).map((o) => ({
    ...o,
    kind: kindOf(o.mediaType, o.fileName || o.path),
  }));
  const withFile = options.filter((o) => !o.missing);
  const campaigns = list.campaigns || [];
  // Próxima a sair primeiro.
  const active = campaigns
    .filter((c) => ACTIVE_CAMPAIGN.includes(c.status))
    .sort((a, b) => new Date(a.scheduledAt || 0) - new Date(b.scheduledAt || 0));
  return {
    ...list,
    options,
    withFile,
    campaigns,
    active,
    hasMissing: options.some((o) => o.missing),
  };
};

const FILTERS = [
  { key: "all", label: "Todas", test: () => true },
  { key: "active", label: "Em campanha ativa", test: (l) => l.active.length > 0 },
  { key: "unused", label: "Sem uso", test: (l) => l.campaigns.length === 0 },
  { key: "problem", label: "Com problema", test: (l) => l.hasMissing },
];

const formatUpdated = (date) => {
  const m = moment(date);
  if (m.isSame(moment(), "day")) return `hoje, ${m.format("HH:mm")}`;
  if (m.isSame(moment().subtract(1, "day"), "day")) return `ontem, ${m.format("HH:mm")}`;
  return m.format("DD/MM/YYYY");
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// ─── Componente ──────────────────────────────────────────────────────────────
const FileLists = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const fetchLists = useCallback(async () => {
    try {
      const { data } = await api.get("/files");
      setLists((data.files || []).map(enrich));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  useEffect(() => {
    if (!user?.companyId) return undefined;
    const socket = socketConnection({ companyId: user.companyId });
    socket.on(`company-${user.companyId}-file`, (data) => {
      if ((data.action === "create" || data.action === "update") && data.fileList) {
        const next = enrich(data.fileList);
        setLists((prev) => {
          const idx = prev.findIndex((l) => l.id === next.id);
          if (idx === -1) return [...prev, next];
          const copy = [...prev];
          copy[idx] = next;
          return copy;
        });
      }
      if (data.action === "delete") {
        setLists((prev) => prev.filter((l) => l.id !== Number(data.fileId)));
      }
      if (data.action === "reload") fetchLists();
    });
    return () => socket.disconnect();
  }, [user, fetchLists]);

  const sorted = useMemo(
    () => [...lists].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [lists]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
    return sorted.filter(
      (l) => f.test(l) && (!q || `${l.name} ${l.message || ""}`.toLowerCase().includes(q))
    );
  }, [sorted, search, filter]);

  const totals = useMemo(() => {
    const files = lists.reduce((s, l) => s + l.withFile.length, 0);
    const bytes = lists.reduce((s, l) => s + (l.totalSize || 0), 0);
    return {
      lists: lists.length,
      files,
      bytes,
      active: lists.filter((l) => l.active.length > 0).length,
      problem: lists.filter((l) => l.hasMissing).length,
    };
  }, [lists]);

  const openNew = () => {
    setEditingId(null);
    setModalOpen(true);
  };
  const openEdit = (list) => {
    setEditingId(list.id);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      await api.delete(`/files/${deleting.id}`);
      setLists((prev) => prev.filter((l) => l.id !== deleting.id));
      toast.success("Lista de arquivos excluída.");
      setDeleting(null);
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingBusy(false);
    }
  };

  // ── peças ──
  const renderThumbs = (list, max = 4) => {
    const shown = list.options.slice(0, max);
    const rest = list.options.length - shown.length;
    return (
      <div className={`${classes.thumbs} ${max <= 2 ? classes.thumbsMobile : ""}`}>
        {shown.map((o) =>
          o.missing ? (
            <span key={o.id} className={`${classes.thumb} ${classes.thumbMissing}`}>?</span>
          ) : (
            <span key={o.id} className={classes.thumb} style={{ backgroundColor: KINDS[o.kind].color }}>
              {KINDS[o.kind].label}
            </span>
          )
        )}
        {rest > 0 && <span className={`${classes.thumb} ${classes.thumbMore}`}>+{rest}</span>}
      </div>
    );
  };

  const renderUsage = (list) => {
    if (list.active.length) {
      const next = list.active[0];
      return (
        <div>
          <Chip
            size="small"
            className={`${classes.pill} ${classes.pillBlue}`}
            label={`${list.active.length} ${list.active.length === 1 ? CAMPAIGN_STATUS[next.status] : "ativas"}`}
          />
          <span className={classes.useSub} title={next.name}>
            {next.name}
            {next.scheduledAt ? ` · ${moment(next.scheduledAt).format("DD/MM [às] HH:mm")}` : ""}
          </span>
        </div>
      );
    }
    if (list.campaigns.length) {
      return (
        <div>
          <Chip
            size="small"
            className={`${classes.pill} ${classes.pillGrey}`}
            label={plural(list.campaigns.length, "campanha", "campanhas")}
          />
          <span className={classes.useSub}>última: {list.campaigns[0].name}</span>
        </div>
      );
    }
    return <span className={classes.subtle}>Nenhuma campanha</span>;
  };

  const renderActions = (list) =>
    isAdmin && (
      <div className={classes.actions}>
        <Tooltip title="Editar">
          <IconButton size="small" className={classes.iconAction} onClick={() => openEdit(list)}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton size="small" className={classes.deleteAction} onClick={() => setDeleting(list)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    );

  const emptyState = (
    <div className={classes.emptyState}>
      <FolderOpenIcon className={classes.emptyIcon} />
      <Typography variant="body2">
        {lists.length === 0
          ? "Nenhuma lista de arquivos ainda. Crie uma para enviar arquivos junto com as campanhas."
          : "Nenhuma lista encontrada para esse filtro."}
      </Typography>
      {lists.length === 0 && isAdmin && (
        <Button color="primary" variant="outlined" size="small" onClick={openNew}>
          Criar primeira lista
        </Button>
      )}
    </div>
  );

  // ── exclusão ──
  const blocked = deleting && deleting.active.length > 0;
  const finished = deleting ? deleting.campaigns.filter((c) => !ACTIVE_CAMPAIGN.includes(c.status)) : [];

  return (
    <MainContainer>
      <FileModal
        open={modalOpen}
        fileListId={editingId}
        onClose={() => setModalOpen(false)}
        reload={fetchLists}
      />

      <Dialog open={Boolean(deleting)} onClose={() => !deletingBusy && setDeleting(null)} maxWidth="xs" fullWidth>
        {deleting && (
          <>
            <DialogTitle>
              {blocked ? "Não é possível excluir agora" : `Excluir "${deleting.name}"?`}
            </DialogTitle>
            <DialogContent>
              {blocked ? (
                <div className={`${classes.banner} ${classes.bannerRed}`}>
                  <ErrorOutlineIcon style={{ color: toneColor(theme, "red") }} fontSize="small" />
                  <span>
                    Esta lista está na campanha <b>"{deleting.active[0].name}"</b>
                    {deleting.active[0].scheduledAt
                      ? `, ${CAMPAIGN_STATUS[deleting.active[0].status]} para ${moment(deleting.active[0].scheduledAt).format("DD/MM [às] HH:mm")}`
                      : ""}
                    . Troque a lista na campanha ou cancele a campanha antes.
                  </span>
                </div>
              ) : (
                <>
                  <Typography variant="body2">Essa ação não pode ser desfeita. Ao excluir:</Typography>
                  <ul className={classes.consequences}>
                    <li>
                      {deleting.withFile.length
                        ? `${plural(deleting.withFile.length, "arquivo", "arquivos")} (${formatBytes(deleting.totalSize)}) ${deleting.withFile.length === 1 ? "é apagado" : "são apagados"} do servidor.`
                        : "A lista não tem arquivos enviados."}
                    </li>
                    {finished.length > 0 && (
                      <li>
                        {plural(finished.length, "campanha", "campanhas")} já{" "}
                        {finished.length === 1 ? "usou" : "usaram"} esta lista e{" "}
                        {finished.length === 1 ? "perde" : "perdem"} a referência. O histórico de envios continua.
                      </li>
                    )}
                  </ul>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button className={classes.dialogButton} variant="outlined" onClick={() => setDeleting(null)} disabled={deletingBusy}>
                {blocked ? "Fechar" : "Cancelar"}
              </Button>
              {!blocked && (
                <Button
                  className={classes.dialogButton}
                  variant="contained"
                  disableElevation
                  onClick={handleDelete}
                  disabled={deletingBusy}
                  style={{ backgroundColor: toneColor(theme, "red"), color: "#fff" }}
                >
                  Excluir lista
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <Title>Lista de arquivos</Title>
            <Typography variant="body2" className={classes.muted}>
              Conjuntos de arquivos enviados junto com as <b>Campanhas</b>.
            </Typography>
          </div>
          {isAdmin && (
            <div className={classes.headerButtons}>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                className={classes.headerButton}
                startIcon={<AddIcon />}
                onClick={openNew}
              >
                Nova lista
              </Button>
            </div>
          )}
        </div>

        <KpiStrip
          classes={classes}
          theme={theme}
          loading={loading}
          items={[
            { label: "Listas", value: totals.lists, caption: `${formatBytes(totals.bytes)} no total`, tone: "grey" },
            { label: "Arquivos", value: totals.files, caption: "somando todas as listas", tone: "grey" },
            { label: "Em campanhas", value: totals.active, caption: "agendadas ou em andamento", tone: "blue" },
            {
              label: "Com problema",
              value: totals.problem,
              caption: "item sem arquivo",
              tone: "amber",
              colorValue: totals.problem > 0,
            },
          ]}
        />

        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.toolbar}>
            <TextField
              className={classes.searchField}
              placeholder="Buscar lista"
              type="search"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ "aria-label": "Buscar lista de arquivos" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <div className={`${classes.chipGroup} ${isMobile ? classes.chipGroupScroll : ""}`} role="group" aria-label="Filtro">
              {FILTERS.map((f) => {
                const on = filter === f.key;
                return (
                  <Chip
                    key={f.key}
                    clickable
                    className={classes.filterChip}
                    color={on ? "primary" : "default"}
                    variant={on ? "default" : "outlined"}
                    aria-pressed={on}
                    label={`${f.label} ${lists.filter(f.test).length}`}
                    onClick={() => setFilter(f.key)}
                  />
                );
              })}
            </div>
          </div>

          <div className={classes.tableWrapper}>
            {!loading && visible.length === 0 && emptyState}

            {isMobile
              ? (visible.length > 0 || loading) && (
                  <div>
                    {visible.map((l) => (
                      <div
                        key={l.id}
                        className={classes.mobileRow}
                        role={isAdmin ? "button" : undefined}
                        tabIndex={isAdmin ? 0 : undefined}
                        onClick={() => isAdmin && openEdit(l)}
                        onKeyDown={(e) => isAdmin && e.key === "Enter" && openEdit(l)}
                      >
                        <div className={classes.thumbsMobile}>
                          {l.options.length ? renderThumbs(l, 2) : (
                            <div className={classes.listIcon}><FolderIcon fontSize="small" /></div>
                          )}
                        </div>
                        <div className={classes.mobileInfo}>
                          <div className={classes.mobileTitle}>{l.name}</div>
                          <div className={classes.mobileTags}>
                            <Chip
                              size="small"
                              className={`${classes.mobilePill} ${classes.pillGrey}`}
                              label={`${plural(l.withFile.length, "arquivo", "arquivos")} · ${formatBytes(l.totalSize)}`}
                            />
                            {l.active.length > 0 && (
                              <Chip size="small" className={`${classes.mobilePill} ${classes.pillBlue}`} label="Em campanha" />
                            )}
                            {l.hasMissing && (
                              <Chip size="small" className={`${classes.mobilePill} ${classes.pillAmber}`} label="Sem arquivo" />
                            )}
                          </div>
                        </div>
                        {isAdmin && (
                          <IconButton
                            size="small"
                            className={classes.deleteAction}
                            aria-label="Excluir"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleting(l);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    ))}
                    {loading && [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
                  </div>
                )
              : (visible.length > 0 || loading) && (
                  <Table stickyHeader size="small" style={{ minWidth: 820 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>Lista</TableCell>
                        <TableCell className={classes.headCell}>Arquivos</TableCell>
                        <TableCell className={classes.headCell}>Usada em</TableCell>
                        <TableCell className={classes.headCell}>Atualizada</TableCell>
                        <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20, width: 100 }}>
                          {isAdmin ? "Ações" : ""}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visible.map((l) => (
                        <TableRow key={l.id} className={classes.row}>
                          <TableCell style={{ paddingLeft: 20 }}>
                            <div className={classes.listCell}>
                              <div className={classes.listIcon}><FolderIcon fontSize="small" /></div>
                              <div style={{ minWidth: 0 }}>
                                <div className={classes.listName}>{l.name}</div>
                                <div className={classes.listDesc}>
                                  {l.message || <i>sem descrição</i>}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {l.options.length ? (
                              <div className={classes.filesCell}>
                                {renderThumbs(l)}
                                <div className={classes.filesText}>
                                  {plural(l.withFile.length, "arquivo", "arquivos")} · {formatBytes(l.totalSize)}
                                  <span className={l.hasMissing ? classes.warnText : classes.useSub} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    {l.hasMissing ? (
                                      <>
                                        <WarningIcon style={{ fontSize: 14 }} />
                                        {plural(l.options.filter((o) => o.missing).length, "item sem arquivo", "itens sem arquivo")}
                                      </>
                                    ) : (
                                      describeKinds(l.withFile)
                                    )}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <span className={classes.subtle}>Nenhum arquivo</span>
                            )}
                          </TableCell>
                          <TableCell>{renderUsage(l)}</TableCell>
                          <TableCell>
                            <span className={classes.subtle}>{formatUpdated(l.updatedAt)}</span>
                          </TableCell>
                          <TableCell style={{ paddingRight: 20 }}>{renderActions(l)}</TableCell>
                        </TableRow>
                      ))}
                      {loading && <TableRowSkeleton columns={5} />}
                    </TableBody>
                  </Table>
                )}
          </div>

          {visible.length > 0 && (
            <div className={classes.footer}>
              Mostrando {visible.length} de {lists.length} · ordenado por nome
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default FileLists;
